import { useEffect, useMemo, useState } from 'react';
import { X } from 'lucide-react';
import { toast } from 'sonner';

import { api } from '../../services/api';
import { FgMovementCreate, FgMovementMode, FgSourceOption, FgSourceType, Item } from '../../services/types';
import { Button } from '../../components/ui/button';
import { RequiredLabel } from '../../components/forms/RequiredLabel';
import { FieldErrorText } from '../../components/forms/FieldErrorText';
import { showValidationSummaryToast } from '../../components/forms/ValidationSummaryToast';
import { focusFirstInvalidField } from '../../components/forms/formValidationUtils';

interface Props {
    isOpen: boolean;
    initialItemId?: number | null;
    items: Item[];
    onClose: () => void;
    onSuccess: () => void;
}

const MODE_OPTIONS: Array<{ value: FgMovementMode; label: string }> = [
    { value: 'RECEIPT', label: 'Receipt (+)' },
    { value: 'ADJUST_IN', label: 'Adjust In (+)' },
    { value: 'ADJUST_OUT', label: 'Adjust Out (-)' },
    { value: 'CUSTOMER_RETURN', label: 'Customer Return (+)' },
];

const SOURCE_TYPES_BY_MODE: Record<FgMovementMode, FgSourceType[]> = {
    RECEIPT: ['WO', 'SO_LINE', 'OPENING_BALANCE', 'COUNT_CORRECTION'],
    ADJUST_IN: ['WO', 'SO_LINE', 'OPENING_BALANCE', 'COUNT_CORRECTION'],
    ADJUST_OUT: ['SO_LINE', 'COUNT_CORRECTION'],
    CUSTOMER_RETURN: ['DN_LINE', 'INVOICE_LINE'],
};

const REASON_OPTIONS_BY_MODE: Record<FgMovementMode, Array<{ value: string; label: string }>> = {
    RECEIPT: [
        { value: 'WO_COMPLETION', label: 'WO Completion' },
        { value: 'INTERNAL_RECEIPT', label: 'Internal Receipt' },
    ],
    ADJUST_IN: [
        { value: 'COUNT_CORRECTION', label: 'Count Correction' },
        { value: 'MANUAL_CORRECTION', label: 'Manual Correction' },
    ],
    ADJUST_OUT: [
        { value: 'COUNT_CORRECTION', label: 'Count Correction' },
        { value: 'SCRAP_REJECT', label: 'Scrap / Reject' },
    ],
    CUSTOMER_RETURN: [
        { value: 'CLIENT_RETURN', label: 'Client Return' },
    ],
};

const SOURCE_TYPE_LABELS: Record<string, string> = {
    WO: 'Work Order Line',
    SO_LINE: 'Sale Order Line',
    DN_LINE: 'Delivery Line',
    INVOICE_LINE: 'Invoice Line',
    OPENING_BALANCE: 'Opening Balance',
    COUNT_CORRECTION: 'Count Correction',
};

const TYPES_REQUIRING_SOURCE_ROW = new Set<FgSourceType>(['WO', 'SO_LINE', 'DN_LINE', 'INVOICE_LINE']);

export function FgMovementModal({ isOpen, initialItemId = null, items, onClose, onSuccess }: Props) {
    const fgItems = useMemo(
        () => items.filter((item) => String(item.item_role || '').toUpperCase() === 'FG'),
        [items],
    );
    const [itemId, setItemId] = useState<string>('');
    const [mode, setMode] = useState<FgMovementMode>('RECEIPT');
    const [sourceType, setSourceType] = useState<FgSourceType>('WO');
    const [sourceId, setSourceId] = useState<number | null>(null);
    const [sourceLineId, setSourceLineId] = useState<number | null>(null);
    const [quantity, setQuantity] = useState<number>(0);
    const [referenceDoc, setReferenceDoc] = useState('');
    const [reasonCode, setReasonCode] = useState('WO_COMPLETION');
    const [sourceOptions, setSourceOptions] = useState<FgSourceOption[]>([]);
    const [isLoadingSources, setIsLoadingSources] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
    const [precheckSummary, setPrecheckSummary] = useState<{ valuation?: string; rate?: number } | null>(null);

    const availableSourceTypes = SOURCE_TYPES_BY_MODE[mode];
    const reasonOptions = REASON_OPTIONS_BY_MODE[mode];
    const requiresSourceRow = TYPES_REQUIRING_SOURCE_ROW.has(sourceType);

    useEffect(() => {
        if (!isOpen) return;
        const firstItem = initialItemId ? String(initialItemId) : (fgItems[0]?.id ? String(fgItems[0].id) : '');
        setItemId(firstItem);
        setMode('RECEIPT');
        setSourceType('WO');
        setSourceId(null);
        setSourceLineId(null);
        setQuantity(0);
        setReferenceDoc('');
        setReasonCode('WO_COMPLETION');
        setFieldErrors({});
        setPrecheckSummary(null);
    }, [isOpen, initialItemId, fgItems]);

    useEffect(() => {
        if (!availableSourceTypes.includes(sourceType)) {
            setSourceType(availableSourceTypes[0]);
        }
    }, [mode]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        setSourceId(null);
        setSourceLineId(null);
        setSourceOptions([]);
        if (!itemId || !requiresSourceRow) return;
        setIsLoadingSources(true);
        api.inventory
            .getFgSourceOptions(parseInt(itemId, 10), mode)
            .then((rows) => {
                setSourceOptions(rows.filter((row) => String(row.source_type || '').toUpperCase() === sourceType));
            })
            .catch((error) => {
                toast.error(error?.message || 'Failed to load source options');
                setSourceOptions([]);
            })
            .finally(() => setIsLoadingSources(false));
    }, [itemId, mode, sourceType, requiresSourceRow]);

    const selectedSource = useMemo(
        () => sourceOptions.find((option) => Number(option.source_id) === Number(sourceId) && Number(option.source_line_id) === Number(sourceLineId)) || null,
        [sourceOptions, sourceId, sourceLineId],
    );

    const validate = (): boolean => {
        const nextErrors: Record<string, string> = {};
        if (!itemId) nextErrors.item_id = 'FG item is required';
        if (!quantity || quantity <= 0) nextErrors.quantity = 'Quantity must be greater than zero';
        if (!sourceType) nextErrors.source_type = 'Source type is required';
        if (requiresSourceRow && (!sourceId || !sourceLineId)) nextErrors.source_row = 'Source record selection is required';
        if (!referenceDoc.trim()) nextErrors.reference_doc = 'Reference is required';
        if (!reasonCode.trim()) nextErrors.reason_code = 'Reason code is required';
        setFieldErrors(nextErrors);
        if (Object.keys(nextErrors).length > 0) {
            showValidationSummaryToast();
            focusFirstInvalidField(Object.keys(nextErrors)[0]);
            return false;
        }
        return true;
    };

    const handleSubmit = async (event: React.FormEvent) => {
        event.preventDefault();
        if (!validate()) return;
        const payload: FgMovementCreate = {
            item_id: parseInt(itemId, 10),
            mode,
            quantity,
            source_type: sourceType,
            source_id: requiresSourceRow ? sourceId : null,
            source_line_id: requiresSourceRow ? sourceLineId : null,
            reference_doc: referenceDoc.trim(),
            reason_code: reasonCode.trim(),
        };
        setIsSubmitting(true);
        try {
            const precheck = await api.inventory.precheckFgMovement(payload);
            if (String(precheck.overall_status || '').toLowerCase() === 'fail') {
                toast.error(precheck.checks?.find((check) => String(check.status).toLowerCase() === 'fail')?.message || 'Precheck failed');
                setIsSubmitting(false);
                return;
            }
            setPrecheckSummary({
                valuation: precheck.valuation_source,
                rate: precheck.cost_rate_applied,
            });
            await api.inventory.createFgMovement(payload);
            toast.success('FG movement posted');
            onSuccess();
        } catch (error: any) {
            toast.error(error?.message || 'Failed to post FG movement');
        } finally {
            setIsSubmitting(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
            <div className="bg-white rounded-lg shadow-xl w-[min(96vw,980px)] mx-4 max-h-[92vh] overflow-y-auto">
                <div className="flex justify-between items-center p-4 border-b">
                    <h2 className="text-lg font-semibold">Post FG Movement</h2>
                    <button onClick={onClose} className="text-slate-500 hover:text-slate-700">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="p-5 space-y-4">
                    <div className="rounded-md border bg-slate-50 p-3 text-xs text-slate-600 space-y-1">
                        <p>Finished-goods movement is source-linked and traceable.</p>
                        <p>Valuation order: WO actual, WAC, material default, explicit SO line fallback.</p>
                        <p>Flow impact: internal FG movement updates inventory only, no accounting journal.</p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <RequiredLabel required htmlFor="item_id">FG Item</RequiredLabel>
                            <select
                                id="item_id"
                                className="w-full rounded border border-slate-300 p-2"
                                value={itemId}
                                onChange={(e) => setItemId(e.target.value)}
                            >
                                <option value="">Select FG item</option>
                                {fgItems.map((item) => (
                                    <option key={item.id} value={item.id}>
                                        {item.code} - {item.name}
                                    </option>
                                ))}
                            </select>
                            <FieldErrorText message={fieldErrors.item_id} />
                        </div>

                        <div>
                            <RequiredLabel required htmlFor="mode">Mode</RequiredLabel>
                            <select
                                id="mode"
                                className="w-full rounded border border-slate-300 p-2"
                                value={mode}
                                onChange={(e) => {
                                    const nextMode = e.target.value as FgMovementMode;
                                    setMode(nextMode);
                                    setReasonCode(REASON_OPTIONS_BY_MODE[nextMode][0]?.value || '');
                                }}
                            >
                                {MODE_OPTIONS.map((option) => (
                                    <option key={option.value} value={option.value}>
                                        {option.label}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div>
                            <RequiredLabel required htmlFor="source_type">Source Type</RequiredLabel>
                            <select
                                id="source_type"
                                className="w-full rounded border border-slate-300 p-2"
                                value={sourceType}
                                onChange={(e) => setSourceType(e.target.value as FgSourceType)}
                            >
                                {availableSourceTypes.map((type) => (
                                    <option key={type} value={type}>
                                        {SOURCE_TYPE_LABELS[type] || type}
                                    </option>
                                ))}
                            </select>
                            <FieldErrorText message={fieldErrors.source_type} />
                        </div>

                        <div>
                            <RequiredLabel required htmlFor="quantity">Quantity</RequiredLabel>
                            <input
                                id="quantity"
                                type="number"
                                step="0.01"
                                className="w-full rounded border border-slate-300 p-2"
                                value={quantity}
                                onChange={(e) => setQuantity(Number(e.target.value))}
                            />
                            <FieldErrorText message={fieldErrors.quantity} />
                        </div>

                        {requiresSourceRow && (
                            <div className="md:col-span-2">
                                <RequiredLabel required htmlFor="source_row">Source Record</RequiredLabel>
                                <select
                                    id="source_row"
                                    className="w-full rounded border border-slate-300 p-2"
                                    disabled={isLoadingSources}
                                    value={sourceId && sourceLineId ? `${sourceId}:${sourceLineId}` : ''}
                                    onChange={(e) => {
                                        const [nextSourceId, nextSourceLineId] = (e.target.value || '').split(':');
                                        setSourceId(nextSourceId ? Number(nextSourceId) : null);
                                        setSourceLineId(nextSourceLineId ? Number(nextSourceLineId) : null);
                                    }}
                                >
                                    <option value="">Select source line</option>
                                    {sourceOptions.map((option) => (
                                        <option
                                            key={`${option.source_type}:${option.source_id}:${option.source_line_id}`}
                                            value={`${option.source_id}:${option.source_line_id}`}
                                        >
                                            {option.label}
                                        </option>
                                    ))}
                                </select>
                                <FieldErrorText message={fieldErrors.source_row} />
                                {selectedSource?.meta && (
                                    <p className="text-xs text-slate-500 mt-1">
                                        Qty: {selectedSource.meta.line_qty ?? '-'}
                                        {selectedSource.meta.so_unit_price ? ` • SO Price: ${Number(selectedSource.meta.so_unit_price).toFixed(2)}` : ''}
                                        {selectedSource.meta.wo_unit_cost ? ` • WO Unit Cost: ${Number(selectedSource.meta.wo_unit_cost).toFixed(2)}` : ''}
                                    </p>
                                )}
                            </div>
                        )}

                        <div>
                            <RequiredLabel required htmlFor="reason_code">Reason Code</RequiredLabel>
                            <select
                                id="reason_code"
                                className="w-full rounded border border-slate-300 p-2"
                                value={reasonCode}
                                onChange={(e) => setReasonCode(e.target.value)}
                            >
                                {reasonOptions.map((option) => (
                                    <option key={option.value} value={option.value}>
                                        {option.label}
                                    </option>
                                ))}
                            </select>
                            <FieldErrorText message={fieldErrors.reason_code} />
                        </div>

                        <div>
                            <RequiredLabel required htmlFor="reference_doc">Reference</RequiredLabel>
                            <input
                                id="reference_doc"
                                type="text"
                                className="w-full rounded border border-slate-300 p-2"
                                value={referenceDoc}
                                onChange={(e) => setReferenceDoc(e.target.value)}
                                placeholder="WO-123 / SO-44 / Return Ticket"
                            />
                            <FieldErrorText message={fieldErrors.reference_doc} />
                        </div>
                    </div>

                    {precheckSummary && (
                        <div className="rounded-md border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600">
                            Valuation: {precheckSummary.valuation || '-'}
                            {typeof precheckSummary.rate === 'number' ? ` • Rate: ${Number(precheckSummary.rate).toFixed(2)}` : ''}
                        </div>
                    )}

                    <div className="flex justify-end gap-2 pt-2">
                        <Button type="button" variant="outline" onClick={onClose} disabled={isSubmitting}>
                            Cancel
                        </Button>
                        <Button type="submit" disabled={isSubmitting}>
                            {isSubmitting ? 'Posting...' : 'Post Movement'}
                        </Button>
                    </div>
                </form>
            </div>
        </div>
    );
}
